import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = index => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const faqItems = [
    {
      question: 'What is a Decentralized Identifier (DID)?',
      answer:
        'A DID is a new type of identifier that enables verifiable, self-sovereign digital identities. Unlike traditional identifiers, DIDs are not controlled by any central authority and can be resolved to DID documents that contain cryptographic public keys and other metadata.',
    },
    {
      question: 'What are Verifiable Credentials?',
      answer:
        'Verifiable Credentials are tamper-evident credentials that can be shared and verified across different services. They are cryptographically secure and allow holders to prove claims about themselves without revealing unnecessary personal information.',
    },
    {
      question: 'How does Documint differ from traditional identity solutions?',
      answer:
        "Documint uses decentralized, user-controlled identity architecture where users own their data. Unlike centralized systems, there's no single point of failure, users control data sharing, and credentials are cryptographically verifiable without relying on the original issuer.",
    },
    {
      question: 'What industries benefit most from self-sovereign identity?',
      answer:
        'Healthcare (patient record control), Education (diploma verification), Finance (KYC compliance), Government (citizen services), and Supply Chain (product authenticity) see significant benefits from implementing SSI solutions.',
    },
    {
      question: 'How does blockchain ensure security for digital identities?',
      answer:
        "Blockchain technology provides a secure and immutable way to store and verify digital identities. It ensures that identity data cannot be tampered with and provides a decentralized verification mechanism that doesn't rely on a single point of failure.",
    },
    {
      question: 'Can I issue my own Verifiable Credentials on the platform?',
      answer:
        'Yes, organizations can issue custom Verifiable Credentials using our platform. Our credential issuance tools support various schemas including educational certificates, employment records, professional licenses, and custom business credentials.',
    },
    {
      question: 'What is zero-knowledge proof in identity verification?',
      answer:
        'Zero-knowledge proofs allow you to prove specific claims about yourself (like being over 18) without revealing the actual data (your exact birthdate). This enhances privacy by sharing only necessary information for verification.',
    },
    {
      question: 'How do I integrate Documint with my existing systems?',
      answer:
        'Documint offers RESTful APIs, SDKs for popular programming languages, and pre-built integrations. Our developer documentation provides step-by-step guides for integration with existing authentication systems and databases.',
    },
    {
      question: 'What happens if I lose my digital wallet?',
      answer:
        "Your credentials are secured with backup and recovery mechanisms. You can restore access using recovery phrases, backup files, or through trusted recovery contacts you've previously designated, ensuring you never permanently lose access to your identity.",
    },
    {
      question: 'Are Documint credentials accepted globally?',
      answer:
        'Documint follows W3C international standards for DIDs and Verifiable Credentials, ensuring global interoperability. However, legal recognition varies by jurisdiction and use case. We work with partners worldwide to expand acceptance.',
    },
    {
      question: 'How can I verify the authenticity of Verifiable Credentials?',
      answer:
        "Verifiable Credentials can be verified using cryptographic proofs and decentralized verification methods. The verification process checks the digital signature, ensures the credential hasn't been tampered with, and confirms the issuer's identity through their DID.",
    },
    {
      question: 'What compliance standards does Documint meet?',
      answer:
        'Documint complies with GDPR, SOC 2, and follows W3C standards for decentralized identity. We also support industry-specific compliance requirements for healthcare (HIPAA), finance (KYC/AML), and education (FERPA) sectors.',
    },
    {
      question: 'Can I revoke or update my Verifiable Credentials?',
      answer:
        'Yes, credential holders and issuers can revoke credentials instantly. Our platform provides revocation registries that allow real-time status checking. For updates, new credentials can be issued while old ones are revoked seamlessly.',
    },
    {
      question: 'What are the costs for using Documint Platform?',
      answer:
        'We offer a freemium model with basic identity creation and verification at no cost. Enterprise features include volume pricing for credential issuance, advanced analytics, custom integrations, and dedicated support with subscription-based plans.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-100 via-surface-200 to-surface-100">
      {/* Hero Section */}
      <section className="pt-24 pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="relative">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-neon-400">
                  {' '}
                  Questions
                </span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-400 to-neon-400 rounded-full mx-auto mb-6"></div>
            </div>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Get answers to common questions about decentralized identities,
              verifiable credentials, and the Documint platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pb-20">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="mb-4"
                >
                  <div className="glass-card overflow-hidden">
                    <button
                      onClick={() => toggleItem(index)}
                      className="w-full text-left p-6 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-semibold text-white pr-4">
                        {item.question}
                      </h3>
                      {openItems[index] ? (
                        <ChevronUp
                          className="text-primary-400 flex-shrink-0"
                          size={24}
                        />
                      ) : (
                        <ChevronDown
                          className="text-primary-400 flex-shrink-0"
                          size={24}
                        />
                      )}
                    </button>

                    <motion.div
                      initial={false}
                      animate={{
                        height: openItems[index] ? 'auto' : 0,
                        opacity: openItems[index] ? 1 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                        {item.answer}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center"
          >
            <div className="glass-card max-w-2xl mx-auto p-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Still Have Questions?
              </h2>
              <p className="text-gray-300 mb-6">
                Can&apos;t find the answer you&apos;re looking for? Our support
                team is here to help.
              </p>
              <motion.a
                href="/support"
                className="btn-primary inline-flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Contact Support</span>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
